import React, { useState } from 'react';
import { MoreVertical, ArrowUpRight, Search, Eye, Trash2 } from 'lucide-react';
import SectionHeader from './SectionHeader';
import CatalogProductImage from './CatalogProductImage';

// Pull the bare shade name out of a "[Finish] Name" string.
const splitShade = (raw) => {
  const s = String(raw || '').trim();
  const m = s.match(/^\[[^\]]+\]\s*(.*)$/);
  return m ? (m[1] || s).trim() : s;
};

// A product reads as "makeup" if its category says so, it carries a real shade,
// OR its name looks like a colour cosmetic. The old shade-only rule missed
// cushions / blush / foundation sticks that have no stored shade (Sofia 2026-07-13).
const MAKEUP_RE = /\b(cushion|foundation|concealer|corrector|bb\s?cream|cc\s?cream|blush(?:er)?|bronzer|highlighter|contour|lipstick|lip\s?tint|lip\s?gloss|lip\s?balm|lip\s?stain|lip\s?liner|mascara|eyeliner|eye\s?liner|eyeshadow|eye\s?shadow|brow|tinted\s?moisturi[sz]er)\b/i;
const isMakeup = (p) => {
  const cat = String(p?.category || p?.product_category || '').toLowerCase();
  if (cat.includes('makeup') || cat.includes('cosmetic')) return true;
  const shade = String(p?.recommended_shade || '').trim();
  if (shade && shade.toUpperCase() !== 'N/A') return true;
  const name = `${p?.name || ''} ${p?.product_name || ''} ${p?.product_type || ''}`;
  return MAKEUP_RE.test(name);
};

// #55: hero-image-led card — larger image, full (un-truncated) title, and the
// shade swatch surfaced as a dot on the hero for makeup.
function ShelfCard({ product, onSelect, onMenu }) {
  const hero = product.image || product.catalog_image_url;
  const shadeName = splitShade(product.recommended_shade);
  const makeup = isMakeup(product);
  const hasSwatch = makeup && (product.swatch_hex || product.swatch_image_url);
  const swatchStyle = {
    backgroundColor: product.swatch_hex || '#E4D2BE',
    backgroundImage: product.swatch_image_url ? `url(${product.swatch_image_url})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };
  return (
    <div className="relative rounded-2xl border border-[#EDE8E0] bg-white overflow-hidden">
      <button type="button" onClick={onSelect} className="block w-full text-left">
        <div className="relative h-[132px] flex items-center justify-center overflow-hidden" style={{ background: '#F4F1EC' }}>
          <CatalogProductImage src={hero} alt={product.name} brand={product.brand} className="w-full h-full object-cover" />
          {hasSwatch && (
            <span className="absolute bottom-2 right-2 rounded-full"
              style={{ width: 22, height: 22, boxShadow: '0 0 0 2px #fff, inset 0 0 0 1px rgba(42,50,75,0.10)', ...swatchStyle }} />
          )}
        </div>
        <div className="px-2.5 pt-2 pb-3">
          <p className="text-[10.5px] uppercase tracking-wide text-[#2A324B]/55 truncate">{product.brand}</p>
          <p className="text-[13px] font-medium text-[#2A1212] leading-snug mt-0.5">{product.name}</p>
          {makeup && shadeName && (
            <div className="flex items-start gap-1.5 mt-1.5">
              {(product.swatch_hex || product.swatch_image_url) && (
                <span className="inline-block rounded-full flex-shrink-0"
                  style={{ width: 10, height: 10, boxShadow: 'inset 0 0 0 1px rgba(42,50,75,0.12)', ...swatchStyle }} />
              )}
              <span className="min-w-0 text-[13px] text-[#2A324B]/70 leading-snug break-words">{shadeName}</span>
            </div>
          )}
        </div>
      </button>
      <button type="button" onClick={onMenu}
        className="absolute top-1.5 right-1.5 h-7 w-7 flex items-center justify-center rounded-full bg-white/85 backdrop-blur-sm text-[#2A324B]/50 hover:bg-white"
        aria-label="Options">
        <MoreVertical className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function ShelfView({ products = {}, onBack, onSelectProduct, onRemoveProduct, onOpenRoutine }) {
  const [tab, setTab] = useState('shelf');   // 'shelf' (owned) | 'wishlist'
  const [cat, setCat] = useState('all');     // 'all' | 'skincare' | 'makeup'
  const [menuFor, setMenuFor] = useState(null); // product whose ⋮ action sheet is open

  const shelfItems = [
    ...(products.current || []),
    ...(products.custom1 || []),
    ...(products.custom2 || []),
  ];
  const wishlistItems = products.wishlist || [];
  const baseItems = tab === 'shelf' ? shelfItems : wishlistItems;
  const items = cat === 'all'
    ? baseItems
    : baseItems.filter((p) => (cat === 'makeup' ? isMakeup(p) : !isMakeup(p)));

  const Tab = ({ id, label, count }) => (
    <button type="button" onClick={() => setTab(id)}
      className="py-3.5 px-1 mr-6 text-[14px] transition-colors"
      style={tab === id
        ? { fontWeight: 600, color: '#2A1212', borderBottom: '2px solid #2A1212' }
        : { fontWeight: 500, color: 'rgba(42,33,18,0.45)' }}>
      {label}{typeof count === 'number' ? ` (${count})` : ''}
    </button>
  );

  const CAT_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'skincare', label: 'Skincare' },
    { key: 'makeup', label: 'Makeup' },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <SectionHeader title="My Shelf" onBack={onBack} className="mb-2" right={
        <button type="button" className="h-11 w-11 flex items-center justify-center rounded-full hover:bg-[#F5F5F5]" aria-label="Search">
          <Search className="h-[18px] w-[18px] text-[#2A324B]/55" />
        </button>
      } />

      <div className="flex items-center border-b border-[#EDE8E0] px-1">
        <Tab id="shelf" label="Owned" count={shelfItems.length} />
        <Tab id="wishlist" label="Wishlist" count={wishlistItems.length} />
        {onOpenRoutine && (
          <button type="button" onClick={onOpenRoutine}
            className="ml-auto py-3.5 px-1 text-[14px] font-medium inline-flex items-center gap-1"
            style={{ color: 'rgba(42,33,18,0.3)' }}>
            Routine <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 py-3 px-1">
        {CAT_FILTERS.map((f) => {
          const on = cat === f.key;
          return (
            <button key={f.key} type="button" onClick={() => setCat(f.key)}
              className="text-[13px] font-medium rounded-full px-3.5 py-1.5 transition-colors"
              style={on
                ? { background: '#2A1212', color: '#fff' }
                : { background: '#fff', color: 'rgba(42,50,75,0.6)', border: '1px solid #E5E0D8' }}>
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="px-1 pb-2">
        {items.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {items.map((p) => (
              <ShelfCard key={p.id} product={p}
                onSelect={() => onSelectProduct?.(p)}
                onMenu={() => setMenuFor(p)} />
            ))}
          </div>
        ) : (
          <div className="py-12 text-center text-[14px] text-[#2A324B]/50">
            {tab === 'shelf'
              ? (cat === 'all'
                  ? 'Nothing here yet — save products from chat to build your shelf.'
                  : `No ${cat} saved yet.`)
              : 'Your wishlist is empty.'}
          </div>
        )}
      </div>

      {/* ⋮ action sheet — Sofia 2026-07-13: the three-dots should DO something,
          not just open the card. Bottom sheet so it isn't clipped by the card. */}
      {menuFor && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={() => setMenuFor(null)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative w-full max-w-md bg-white rounded-t-3xl p-2 pb-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-4 pt-3 pb-2">
              {menuFor.brand && <p className="text-[13px] uppercase tracking-wide text-[#2A324B]/50 truncate">{menuFor.brand}</p>}
              <p className="text-[15px] font-semibold text-[#2A1212] leading-tight truncate">{menuFor.name}</p>
            </div>
            <button type="button" onClick={() => { const p = menuFor; setMenuFor(null); onSelectProduct?.(p); }}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-[15px] text-[#2A1212] hover:bg-[#F7F2EC] rounded-xl">
              <Eye className="h-[18px] w-[18px] text-[#2A324B]/60" /> View details
            </button>
            {onRemoveProduct && (
              <button type="button" onClick={() => { const p = menuFor; setMenuFor(null); onRemoveProduct(p); }}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-[15px] text-[#B4453A] hover:bg-[#F9EEEC] rounded-xl">
                <Trash2 className="h-[18px] w-[18px]" /> Remove from shelf
              </button>
            )}
            <button type="button" onClick={() => setMenuFor(null)}
              className="w-full mt-1 px-4 py-3 text-[14px] font-medium text-[#2A324B]/60 hover:bg-[#F5F5F5] rounded-xl">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
